/**
 * @file capture/preferredMime.ts
 * @brief Pick the best MediaRecorder MIME (Safari → mp4/AAC; Chrome → WebM/Opus).
 *
 * @author DES
 * @date 28-07-2026
 */

import { isSafariWebKit } from "./safariDetect";

const WEBM_FIRST = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/ogg;codecs=opus",
] as const;

/** Safari/iOS WebKit: mp4 first — WebM MediaRecorder is unreliable or absent. */
const MP4_FIRST = [
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/aac",
  "audio/webm;codecs=opus",
  "audio/webm",
] as const;

export type PreferredMime = {
  mimeType: string;
  extension: string;
  contentType: string;
};

function extensionFor(mime: string): string {
  if (mime.includes("mp4") || mime.includes("aac")) return "m4a";
  if (mime.includes("ogg")) return "ogg";
  return "webm";
}

function contentTypeFor(mime: string): string {
  if (mime.includes("mp4") || mime.includes("aac")) return "audio/mp4";
  return mime.split(";")[0] ?? "audio/webm";
}

export function pickPreferredMime(): PreferredMime {
  if (typeof MediaRecorder === "undefined") {
    const safari = isSafariWebKit();
    return {
      mimeType: "",
      extension: safari ? "m4a" : "webm",
      contentType: safari ? "audio/mp4" : "audio/webm",
    };
  }

  const candidates = isSafariWebKit() ? MP4_FIRST : WEBM_FIRST;

  for (const mime of candidates) {
    if (MediaRecorder.isTypeSupported(mime)) {
      return {
        mimeType: mime,
        extension: extensionFor(mime),
        contentType: contentTypeFor(mime),
      };
    }
  }

  return {
    mimeType: "",
    extension: isSafariWebKit() ? "m4a" : "webm",
    contentType: isSafariWebKit() ? "audio/mp4" : "audio/webm",
  };
}
